import type { IOrganizationGroupDetail } from "@/api/organization-groups";
import {
    organizationGroupErrorMessage,
    useDeleteOrganizationGroup,
} from "@/api/organization-groups";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useId, useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useNavigate } from "react-router";
import { memberLabel } from "./columns";

interface DeleteGroupButtonProps {
    group: IOrganizationGroupDetail;
}

function joinClauses(clauses: string[]) {
    if (clauses.length < 2) return clauses.join("");
    return `${clauses.slice(0, -1).join(", ")} and ${clauses[clauses.length - 1]}`;
}

function blockingReason(group: IOrganizationGroupDetail) {
    const blockers: string[] = [];
    if (group.members.length > 0) {
        blockers.push(`remove ${memberLabel(group.members.length)}`);
    }
    if (group.assigned_skills.length > 0) {
        const names = group.assigned_skills
            .map((skill) => skill.name)
            .join(", ");
        blockers.push(`unassign ${names}`);
    }
    if (group.assigned_checklists.length > 0) {
        const noun =
            group.assigned_checklists.length === 1 ? "checklist" : "checklists";
        const names = group.assigned_checklists
            .map((checklist) => checklist.name)
            .join(", ");
        blockers.push(`unassign ${noun} ${names}`);
    }
    if (blockers.length === 0) return null;
    const reason = joinClauses(blockers);
    return `${reason.charAt(0).toUpperCase()}${reason.slice(1)} before deleting this group.`;
}

export function DeleteGroupButton({ group }: DeleteGroupButtonProps) {
    const [confirming, setConfirming] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const navigate = useNavigate();
    const deleteGroup = useDeleteOrganizationGroup();
    const deletion = useSingleFlight();
    const reasonId = useId();

    const reason = blockingReason(group);
    const blocked = reason !== null;
    const handleConfirmingChange = (next: boolean) => {
        if (!next && deletion.isRunning()) return;
        setConfirming(next);
    };

    const confirm = async () => {
        setFailure(null);
        await deletion.run(async () => {
            try {
                await deleteGroup.mutateAsync(group.id);
                setConfirming(false);
                navigate("/admin/organization-groups");
            } catch (error) {
                setFailure(
                    organizationGroupErrorMessage(
                        error,
                        "Couldn't delete the group. Please try again."
                    )
                );
            }
        });
    };

    const button = (
        <Button
            type="button"
            variant="destructive"
            aria-disabled={blocked || deleteGroup.isPending}
            aria-describedby={blocked ? reasonId : undefined}
            onClick={() => {
                if (blocked || deleteGroup.isPending) return;
                setFailure(null);
                setConfirming(true);
            }}
        >
            Delete group
        </Button>
    );

    return (
        <>
            {blocked ? (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger
                            render={<span className="inline-flex" />}
                        >
                            {button}
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[40ch]">
                            {reason}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                button
            )}
            {blocked && (
                <span id={reasonId} className="sr-only">
                    {reason}
                </span>
            )}

            <Dialog open={confirming} onOpenChange={handleConfirmingChange}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete group</DialogTitle>
                        <DialogDescription>
                            {group.name} will be deleted and its shared folder
                            will stop being shared. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    {failure && (
                        <p role="alert" className="text-destructive text-sm">
                            {failure}
                        </p>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleConfirmingChange(false)}
                            disabled={deleteGroup.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={confirm}
                            aria-disabled={deleteGroup.isPending}
                            disabled={deleteGroup.isPending}
                        >
                            Delete group
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
